import { capturePerformanceAppBoot, trackEvent } from "@/domains/metrics";
import { useAppStore } from "@/store";
import { useEffect, useRef } from "react";
import { TrackedEvent } from "@/domains/metrics";

interface PerformanceMemory {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

interface ExtendedPerformance extends Performance {
  memory?: PerformanceMemory;
}

export const useAuthenticatedMetrics = () => {
  const { store } = useAppStore();

  // User Identification
  const { myPersonalSpaceAccountId } = store.spaceAccounts;

  // Performance / Bootstrap
  useEffect(() => {
    if (myPersonalSpaceAccountId && store.sync.bootstrapDuration) {
      trackEvent(TrackedEvent.PerformanceBootstrap, {
        seconds: store.sync.bootstrapDuration / 1000,
      });
    }
  }, [myPersonalSpaceAccountId, store.sync.bootstrapDuration]);

  // Performance / Heartbeat
  const poolSizes = {
    notes: store.notes.pool.size,
    collections: store.collections.pool.size,
  };
  const poolSizesRef = useRef(poolSizes);
  poolSizesRef.current = poolSizes;

  useEffect(() => {
    if (!myPersonalSpaceAccountId) return;

    /**
     * @note (from Kevin):
     *
     * I commented out the counts here (e.g. num_notes) for now because I'm concerned that they:
     * (a) May not be accurate because they only reflect what's in the in-memory pools
     * (b) May be expensive and non-performant to compute
     */
    capturePerformanceAppBoot({
      // num_notes: store.notes.all.length,
      // num_collections: store.collections.all.length,
      // num_sync_events: // TODO,
    });

    const trackPerformanceHeartbeat = () => {
      const perf = performance as ExtendedPerformance;
      const numNotesInListInMemory = poolSizesRef.current.notes;
      const numCollectionsInMemory = poolSizesRef.current.collections;

      trackEvent(TrackedEvent.PerformanceHeartbeat, {
        numNotesInListInMemory,
        numCollectionsInMemory,
        memory_mb:
          hasMemory(perf) && perf.memory
            ? Math.round(perf.memory.usedJSHeapSize / 1024 / 1024)
            : undefined,
      });
    };

    /**
     * Track performance heartbeat after 60s, then every 60m thereafter
     */
    let interval: NodeJS.Timeout;
    const timeout = setTimeout(() => {
      trackPerformanceHeartbeat();
      interval = setInterval(trackPerformanceHeartbeat, 60 * 60 * 1000);
    }, 60 * 1000);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [myPersonalSpaceAccountId]);
};

export const useAnonMetrics = () => {
  useEffect(() => {
    // We don't want to polute metrics with anonymous data
    // capturePerformanceAppBoot({});
  }, []);
};

function hasMemory(performance: Performance): performance is ExtendedPerformance {
  return "memory" in performance && performance.memory !== undefined;
}
